import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '../routes/routes';
import SecureStorage from '../utils/secureStorage';

interface UserDropdownProps {
  userEmail?: string;
}

const UserDropdown = ({ userEmail }: UserDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    SecureStorage.removeCustomerToken();
    navigate(PATHS.CUSTOMER_SIGNIN);
  };

  const menuItems = [
    // {
    //   id: 'profile',
    //   label: 'Profile',
    //   icon: (
    //     <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
    //       <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    //     </svg>
    //   ),
    //   onClick: () => {
    //     // TODO: Navigate to profile page when implemented
    //     console.log('Profile clicked');
    //     setIsOpen(false);
    //   }
    // },
    // {
    //   id: 'settings',
    //   label: 'Settings',
    //   icon: (
    //     <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
    //       <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0l1.83 7.545a.75.75 0 01-.655.862H9.12a.75.75 0 01-.655-.862l1.83-7.545z" />
    //       <path strokeLinecap="round" strokeLinejoin="round" d="M18 8.25H6a3 3 0 00-3 3v6a3 3 0 003 3h12a3 3 0 003-3v-6a3 3 0 00-3-3z" />
    //     </svg>
    //   ),
    //   onClick: () => {
    //     // TODO: Navigate to settings page when implemented
    //     console.log('Settings clicked');
    //     setIsOpen(false);
    //   }
    // },
    // {
    //   id: 'help',
    //   label: 'Help & Support',
    //   icon: (
    //     <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
    //       <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    //     </svg>
    //   ),
    //   onClick: () => {
    //     // TODO: Navigate to help page when implemented
    //     console.log('Help clicked');
    //     setIsOpen(false);
    //   }
    // },
    {
      id: 'logout',
      label: 'Logout',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
      ),
      onClick: handleLogout,
      isDestructive: true
    }
  ];

  return (
    <div className="relative" ref={dropdownRef}>
      {/* User Avatar Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-user w-5 h-5" data-lov-id="src/components/VivaClubHeader.tsx:38:12" data-lov-name="User" data-component-path="src/components/VivaClubHeader.tsx" data-component-line="38" data-component-file="VivaClubHeader.tsx" data-component-name="User" data-component-content="%7B%22className%22%3A%22w-5%20h-5%22%7D"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 max-w-[90vw] bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
          {/* User Info Header */}
          {userEmail && (
            <div className="px-4 py-3 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-900 mb-1">Signed in as</p>
              <p className="text-sm text-gray-600 break-all leading-snug">{userEmail}</p>
            </div>
          )}

          {/* Menu Items */}
          <div className="py-1">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={item.onClick}
                className={`w-full flex items-center px-4 py-2 text-sm text-left hover:bg-gray-50 transition-colors ${
                  item.isDestructive 
                    ? 'text-red-600 hover:bg-red-50' 
                    : 'text-gray-700'
                }`}
              >
                <span className="mr-3">{item.icon}</span>
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDropdown;
